import React, { useState } from 'react';
import { 
  enterpriseExamples, 
  EnterpriseProjectConfigurationManager 
} from '../data/enterprise-demo-data';

// Simple UI components for the demo (using inline styles for compatibility)
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div style={{
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    ...className === 'space-y-6' ? { marginBottom: '24px' } : {}
  }}>
    {children}
  </div>
);

const CardHeader = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>{children}</div>
);

const CardTitle = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <h3 style={{ 
    fontSize: '18px', 
    fontWeight: '600',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}>{children}</h3>
);

const CardContent = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div style={{ padding: '16px' }}>{children}</div>
);

const Button = ({ 
  children, 
  onClick, 
  variant = 'default',
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'outline';
  className?: string;
}) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '500',
      border: variant === 'outline' ? '1px solid #d1d5db' : 'none',
      backgroundColor: variant === 'outline' ? 'white' : '#2563eb',
      color: variant === 'outline' ? '#374151' : 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      margin: '4px'
    }}
    onMouseOver={(e) => {
      if (variant === 'outline') {
        e.currentTarget.style.backgroundColor = '#f9fafb';
      } else {
        e.currentTarget.style.backgroundColor = '#1d4ed8';
      }
    }}
    onMouseOut={(e) => {
      if (variant === 'outline') {
        e.currentTarget.style.backgroundColor = 'white';
      } else {
        e.currentTarget.style.backgroundColor = '#2563eb';
      }
    }}
  >
    {children}
  </button>
);

const Badge = ({ 
  children, 
  variant = 'default', 
  className = '' 
}: { 
  children: React.ReactNode; 
  variant?: 'default' | 'secondary' | 'outline' | 'destructive';
  className?: string;
}) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: variant === 'secondary' 
      ? '#f3f4f6' 
      : variant === 'outline'
      ? 'transparent'
      : variant === 'destructive'
      ? '#fef2f2'
      : '#dbeafe',
    color: variant === 'secondary' 
      ? '#1f2937'
      : variant === 'outline'
      ? '#374151'
      : variant === 'destructive'
      ? '#dc2626'
      : '#1e40af',
    border: variant === 'outline' ? '1px solid #d1d5db' : 'none'
  }}>
    {children}
  </span>
);

// Simple Tabs implementation
const Tabs = ({ 
  children, 
  defaultValue, 
  className = '' 
}: { 
  children: React.ReactNode; 
  defaultValue: string;
  className?: string;
}) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  
  return (
    <div style={{ width: '100%' }} data-active-tab={activeTab}>
      {React.Children.map(children, child =>
        React.isValidElement(child) 
          ? React.cloneElement(child, { activeTab, setActiveTab } as any)
          : child
      )}
    </div>
  );
};

const TabsList = ({ 
  children, 
  className = '',
  activeTab,
  setActiveTab
}: { 
  children: React.ReactNode; 
  className?: string;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}) => (
  <div style={{ 
    display: 'flex', 
    borderBottom: '1px solid #e5e7eb',
    gap: '0'
  }}>
    {React.Children.map(children, child =>
      React.isValidElement(child) 
        ? React.cloneElement(child, { activeTab, setActiveTab } as any)
        : child
    )}
  </div>
);

const TabsTrigger = ({ 
  children, 
  value,
  activeTab,
  setActiveTab
}: { 
  children: React.ReactNode; 
  value: string;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}) => (
  <button
    onClick={() => setActiveTab?.(value)}
    style={{
      padding: '8px 16px',
      fontWeight: '500',
      fontSize: '14px',
      border: 'none',
      backgroundColor: 'transparent',
      borderBottom: activeTab === value ? '2px solid #2563eb' : '2px solid transparent',
      color: activeTab === value ? '#2563eb' : '#6b7280',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onMouseOver={(e) => {
      if (activeTab !== value) {
        e.currentTarget.style.color = '#374151';
      }
    }}
    onMouseOut={(e) => {
      if (activeTab !== value) {
        e.currentTarget.style.color = '#6b7280';
      }
    }}
  >
    {children}
  </button>
);

const TabsContent = ({ 
  children, 
  value,
  className = '',
  activeTab
}: { 
  children: React.ReactNode; 
  value: string;
  className?: string;
  activeTab?: string;
}) => (
  activeTab === value ? <div style={{ marginTop: '16px' }}>{children}</div> : null
);

/**
 * Enterprise Configuration Demonstration Component
 * Shows how SAP and Oracle-style configurations work in SQLHealthPro
 */
export const EnterpriseConfigDemo: React.FC = () => {
  const [selectedConfig, setSelectedConfig] = useState<'sap' | 'oracle'>('sap');
  const [currentProject, setCurrentProject] = useState<any>(
    enterpriseExamples.sapS4HanaMigration
  );

  const switchToSAP = () => {
    setSelectedConfig('sap');
    setCurrentProject(enterpriseExamples.sapS4HanaMigration);
  };

  const switchToOracle = () => {
    setSelectedConfig('oracle');
    setCurrentProject(enterpriseExamples.oracleCloudERP);
  };

  const config = currentProject.getConfiguration();
  const workflowConfig = currentProject.getWorkflowConfiguration();
  const securityConfig = currentProject.getSecurityConfiguration();

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
          Enterprise Project Configuration Demo
        </h1>
        <p style={{ fontSize: '18px', color: '#6b7280', margin: '0 0 24px 0' }}>
          Experience how SAP and Oracle-style project configurations work in SQLHealthPro
        </p>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '24px' }}>
          <Button 
            onClick={switchToSAP}
            variant={selectedConfig === 'sap' ? 'default' : 'outline'}
          >
            🟦 SAP S/4HANA Style
          </Button>
          <Button 
            onClick={switchToOracle}
            variant={selectedConfig === 'oracle' ? 'default' : 'outline'}
          >
            🔴 Oracle Fusion Style
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        {/* Project Overview */}
        <Card>
          <CardHeader>
            <CardTitle>
              <span>📋 Project Overview</span>
              <Badge variant="secondary">{config.projectType.category.toUpperCase()}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontWeight: '600', margin: '0 0 4px 0' }}>Project Name</h4>
              <p style={{ color: '#6b7280', margin: '0' }}>{config.projectName}</p>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontWeight: '600', margin: '0 0 4px 0' }}>Project Type</h4>
              <p style={{ color: '#6b7280', margin: '0 0 4px 0' }}>{config.projectType.name}</p>
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: '0' }}>{config.projectType.description}</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontWeight: '600', margin: '0 0 8px 0' }}>Complexity</h4>
              <Badge variant={config.projectType.complexity === 'enterprise' ? 'destructive' : 'secondary'}>
                {config.projectType.complexity.toUpperCase()}
              </Badge>
            </div>

            <div>
              <h4 style={{ fontWeight: '600', margin: '0 0 4px 0' }}>Industry</h4>
              <p style={{ color: '#6b7280', margin: '0' }}>{config.projectType.industry || 'General'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Terminology Mapping */}
        <Card>
          <CardHeader>
            <CardTitle>🔄 Dynamic Terminology</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(config.terminology).map(([key, value]) => (
                <div key={key} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '8px', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '6px' 
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', textTransform: 'capitalize' }}>{key}</span>
                  <span style={{ fontSize: '14px', color: '#2563eb', fontWeight: '600' }}>{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="phases">
        <TabsList>
          <TabsTrigger value="phases">🔄 Project Phases</TabsTrigger>
          <TabsTrigger value="workflow">⚙️ Workflow Config</TabsTrigger>
          <TabsTrigger value="security">🔒 Security & Access</TabsTrigger>
          <TabsTrigger value="menu">📋 Menu Structure</TabsTrigger>
        </TabsList>

        <TabsContent value="phases">
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedConfig === 'sap' ? '🟦 SAP ASAP Methodology Phases' : '🔴 Oracle AIM Methodology Phases'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                {config.enabledPhases.map((phaseId, index) => {
                  // Mock phase data since we can't import from the file due to path issues
                  const mockPhases = selectedConfig === 'sap' ? [
                    { id: 'project-preparation', name: 'Project Preparation', description: 'Project setup and organization', icon: '⚙️' },
                    { id: 'business-blueprint', name: 'Business Blueprint', description: 'Business process design', icon: '📋' },
                    { id: 'realization', name: 'Realization', description: 'System configuration and development', icon: '💻' },
                    { id: 'final-preparation', name: 'Final Preparation', description: 'Testing and go-live prep', icon: '✅' },
                    { id: 'go-live-support', name: 'Go-Live & Support', description: 'Production deployment', icon: '🚀' }
                  ] : [
                    { id: 'initiate', name: 'Initiate', description: 'Project initiation and planning', icon: '▶️' },
                    { id: 'plan', name: 'Plan', description: 'Detailed planning and design', icon: '📅' },
                    { id: 'execute', name: 'Execute', description: 'Implementation and development', icon: '🛠️' },
                    { id: 'control', name: 'Monitor & Control', description: 'Project monitoring', icon: '📊' },
                    { id: 'close', name: 'Close', description: 'Project closure', icon: '✅' }
                  ];
                  
                  const phase = mockPhases[index];
                  if (!phase) return null;

                  return (
                    <div key={phaseId} style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '16px',
                      backgroundColor: 'white'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '600' }}>Phase {index + 1}</span>
                        <span style={{ fontSize: '20px' }}>{phase.icon}</span>
                      </div>
                      <h4 style={{ fontWeight: '500', margin: '0 0 8px 0' }}>{phase.name}</h4>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>{phase.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflow">
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedConfig === 'sap' ? '🟦 SAP Project System Configuration' : '🔴 Oracle PPM Configuration'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {workflowConfig && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <h4 style={{ fontWeight: '600', margin: '0 0 8px 0' }}>Workflow Type</h4>
                    <Badge variant="secondary">{workflowConfig.type}</Badge>
                  </div>

                  {selectedConfig === 'sap' && (
                    <div>
                      <h4 style={{ fontWeight: '600', margin: '0 0 12px 0' }}>🟦 SAP Profiles</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ padding: '8px', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Project Profile</div>
                          <code style={{ fontSize: '12px', color: '#1e40af' }}>SAP_PROJECT_001</code>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Status Profile</div>
                          <code style={{ fontSize: '12px', color: '#1e40af' }}>SAP_STATUS_001</code>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedConfig === 'oracle' && (
                    <div>
                      <h4 style={{ fontWeight: '600', margin: '0 0 12px 0' }}>🔴 Oracle Templates</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Task Template</div>
                          <code style={{ fontSize: '12px', color: '#dc2626' }}>ORACLE_TASK_TEMPLATE</code>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Project Class</div>
                          <code style={{ fontSize: '12px', color: '#dc2626' }}>TECHNOLOGY</code>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>🔒 Enterprise Security & Access Control</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <h4 style={{ fontWeight: '600', margin: '0 0 12px 0' }}>Security Features</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(securityConfig).map(([feature, enabled]) => (
                      <div key={feature} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ textTransform: 'capitalize' }}>{feature.replace(/([A-Z])/g, ' $1')}</span>
                        <Badge variant={enabled ? 'default' : 'secondary'}>
                          {enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 style={{ fontWeight: '600', margin: '0 0 12px 0' }}>
                    {selectedConfig === 'sap' ? '🟦 SAP Authorizations' : '🔴 Oracle Security Model'}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedConfig === 'sap' ? (
                      <>
                        <div style={{ padding: '8px', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Authorization Object</div>
                          <code style={{ fontSize: '12px' }}>PS_PROJECT</code>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Transaction Codes</div>
                          <code style={{ fontSize: '12px' }}>CJ01, CJ02, CJ03</code>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Function Security</div>
                          <code style={{ fontSize: '12px' }}>PA_PROJECT_SUPER_USER</code>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>Organization Access</div>
                          <code style={{ fontSize: '12px' }}>PA_ORG_AUTHORITY</code>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="menu">
          <Card>
            <CardHeader>
              <CardTitle>📋 Dynamic Menu Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0' }}>
                Menu categories and subcategories are dynamically generated based on project type and configuration.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {Object.entries(config.menuOverrides).slice(0, 3).map(([phaseId, phaseConfig]) => (
                  <div key={phaseId} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                    <h4 style={{ fontWeight: '600', textTransform: 'capitalize', margin: '0 0 12px 0' }}>
                      {phaseId} Phase
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                      {phaseConfig.categories.slice(0, 3).map((category: any) => (
                        <div key={category.id} style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ fontWeight: '500' }}>{category.name}</span>
                            <Badge variant="outline">
                              {category.priority}
                            </Badge>
                          </div>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>{category.description}</p>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {category.subcategories.length} subcategories
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>💾 Configuration Export</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
              Export the current project configuration for backup, sharing, or deployment.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button 
                onClick={() => {
                  const config = currentProject.exportConfiguration();
                  navigator.clipboard.writeText(config);
                  alert('Configuration copied to clipboard!');
                }}
                variant="outline"
              >
                📋 Copy Configuration
              </Button>
              <Button 
                onClick={() => {
                  const config = currentProject.exportConfiguration();
                  const blob = new Blob([config], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${selectedConfig}-project-config.json`;
                  a.click();
                }}
                variant="outline"
              >
                💾 Download JSON
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnterpriseConfigDemo;
